/**
 * Pure comparison of the renderer's legacy lifecycle against the Remix state machine.
 *
 * Both phases are expressed in the renderer's existing `RendererRemixLifecyclePhase` vocabulary, so
 * diagnostics introduce no second normalization of renderer state. Nothing here reads or mutates
 * runtime state: callers pass two phases plus whether the pair has settled, and get back a
 * classification. Timing is the caller's concern.
 */
import type {
	RendererRemixLifecyclePhase,
	RendererRemixTerminalPhase,
} from './rendererRemixLifecycle.ts';

/**
 * Where in the renderer's own flow a comparison was taken.
 *
 * Anchored to the legacy phase, which is derived purely from the renderer's own signals and so is
 * the side independent of the machine. Deriving it from the machine phase instead would make it a
 * relabeling of `machinePhase` and add no grouping dimension to the monitoring query.
 */
export type RendererRemixComparisonPoint =
	| 'flow-started'
	| 'result-ready'
	| 'preview-opened'
	| 'submitted'
	| 'error'
	| 'aborted'
	| 'released';

export type RendererRemixDivergenceReason =
	| 'legacy-not-tracking'
	| 'terminal-outcome-mismatch'
	| 'unexpected-phase-pair';

export type RendererRemixPendingReason = 'machine-not-tracking' | 'awaiting-stable-phases';

export type RendererRemixDivergence = Readonly<{
	legacyPhase: RendererRemixLifecyclePhase;
	machinePhase: RendererRemixLifecyclePhase;
	comparisonPoint: RendererRemixComparisonPoint;
	divergenceReason: RendererRemixDivergenceReason;
}>;

export type RendererRemixParityResult =
	| Readonly<{ outcome: 'match' }>
	| Readonly<{ outcome: 'pending'; reason: RendererRemixPendingReason }>
	| Readonly<{ outcome: 'divergence'; divergence: RendererRemixDivergence }>;

const COMPARISON_POINT_BY_LEGACY_PHASE = {
	inactive: 'released',
	thinking: 'flow-started',
	ready: 'result-ready',
	previewing: 'preview-opened',
	submitting: 'submitted',
	error: 'error',
	aborted: 'aborted',
} as const satisfies Record<RendererRemixLifecyclePhase, RendererRemixComparisonPoint>;

export const comparisonPointForLegacyPhase = (
	legacyPhase: RendererRemixLifecyclePhase,
): RendererRemixComparisonPoint => COMPARISON_POINT_BY_LEGACY_PHASE[legacyPhase];

/** A phase from which no further lifecycle transition is expected. */
export const isTerminalPhase = (
	phase: RendererRemixLifecyclePhase,
): phase is RendererRemixTerminalPhase => phase === 'error' || phase === 'aborted';

export const classifyDivergenceReason = (
	legacyPhase: RendererRemixLifecyclePhase,
	machinePhase: RendererRemixLifecyclePhase,
): RendererRemixDivergenceReason => {
	// The machine is running a flow the renderer no longer considers active.
	if (legacyPhase === 'inactive') {
		return 'legacy-not-tracking';
	}

	if (isTerminalPhase(legacyPhase) || isTerminalPhase(machinePhase)) {
		return 'terminal-outcome-mismatch';
	}

	return 'unexpected-phase-pair';
};

/**
 * Classifies one legacy/machine phase pair.
 *
 * `isStable` says whether the pair has held long enough to be treated as settled. A differing pair
 * that has not settled is `pending` rather than a divergence, which is what keeps ordinary
 * asynchronous transition skew out of the signal.
 */
export const compareRendererRemixParity = ({
	legacyPhase,
	machinePhase,
	isStable,
}: Readonly<{
	legacyPhase: RendererRemixLifecyclePhase;
	machinePhase: RendererRemixLifecyclePhase;
	isStable: boolean;
}>): RendererRemixParityResult => {
	// `inactive` covers both admission skew and a released machine flow. Machine release clears the
	// imperative remixId ref but retains the React remixId state, so this observer can continue
	// reading the released flow for the remaining modal lifetime. Comparing here would therefore
	// report normal teardown as a divergence; terminal pairs must be captured before the machine is
	// released. The machine's own release paths are instrumented separately by
	// `useRendererRemixMachineLifecycle`.
	if (machinePhase === 'inactive') {
		return { outcome: 'pending', reason: 'machine-not-tracking' };
	}

	if (legacyPhase === machinePhase) {
		return { outcome: 'match' };
	}

	if (!isStable) {
		return { outcome: 'pending', reason: 'awaiting-stable-phases' };
	}

	return {
		outcome: 'divergence',
		divergence: {
			legacyPhase,
			machinePhase,
			comparisonPoint: comparisonPointForLegacyPhase(legacyPhase),
			divergenceReason: classifyDivergenceReason(legacyPhase, machinePhase),
		},
	};
};
